// --- React & Redux
import React from "react";
import { useSelector } from "react-redux";
// --- Components
import { DetailsModal } from "tap/components/DetailsModal/index.jsx";
import { JobViewModalHeader } from "tap/components/JobViewModalHeader/index.jsx";
import { JobViewGrid } from "./grid";
import { PaginationBar } from "tap/components/PaginationBar/index.jsx";
// --- Hooks & Actions
import { useUpdateEngineData } from "../../../hooks/useUpdateEngineData";
import { useUpdateEngineActions } from "tap/store/updateEngine/updateEngine.js";
import { useStoredModalItemsPerPageSync } from "tap/utils/hooks/useStoredItemsPerPageSync.js";
import { PAGINATION_STORAGE_KEYS } from "tap/utils/paginationPreferences.js";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
// --- Utils
import { tapApi } from "tap/api.js";
// --- Styles
import "./styles.scss";

export const JobStatusModal = () => {
  const {
    isJobViewModalOpen,
    jobViewTotalPages,
    jobViewCurrentPage,
    jobViewTotalItems,
    jobViewItemsPerPage,
    selectedJob,
    jobViewFilterForm,
    jobTasks,
    pendingStatuses,
  } = useSelector((state) => state.updateEngine);
  const { fetchJobs, updateUpdateEngine, updateEngineRange } =
    useUpdateEngineData();
  const { clearJobViewModal } = useUpdateEngineActions();
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast } = useToasts();

  useStoredModalItemsPerPageSync({
    storageKey: PAGINATION_STORAGE_KEYS.updateEngineJobView,
    isOpen: isJobViewModalOpen,
    itemsPerPage: jobViewItemsPerPage,
    onSync: updateUpdateEngine,
    itemsPerPageField: "jobViewItemsPerPage",
    currentPageField: "jobViewCurrentPage",
  });

  const onCloseModal = async () => {
    clearJobViewModal();
    const data = await fetchJobs(updateEngineRange, null, null, true);
    if (data) {
      updateUpdateEngine({
        jobs: data?.content,
        totalPages: data?.totalPages,
        totalItems: data?.totalElements,
        currentPage: data?.pageable?.pageNumber,
      });
    }
  };

  const handlePageChange = async (pageNumber) => {
    setLoadingTrue();
    try {
      const apiPage = pageNumber + 1;
      const job = await tapApi.job(selectedJob?.id, false);
      const data = await tapApi.jobViewTasks(
        selectedJob?.id,
        false,
        apiPage,
        jobViewItemsPerPage,
        jobViewFilterForm.taskStatus.value,
      );

      if (
        data?.code ||
        data?.httpCode ||
        data?.error ||
        job?.code ||
        job?.httpCode ||
        job?.error
      ) {
        showErrorToast(data?.message || "Failed to fetch job tasks data.");
        return;
      }
      updateUpdateEngine({
        selectedJob: job,
        jobTasks: data?.tasks,
        jobViewTotalPages: data?.totalPages,
        jobViewTotalItems: data?.totalElements ?? data?.tasks?.length ?? 0,
        jobViewCurrentPage: pageNumber,
      });
    } catch (error) {
      showErrorToast(error?.message || "Failed to fetch job tasks data.");
    } finally {
      setLoadingFalse();
    }
  };

  const handlePageSizeChange = async (newSize) => {
    setLoadingTrue();
    try {
      const job = await tapApi.job(selectedJob?.id, false);
      const data = await tapApi.jobViewTasks(
        selectedJob?.id,
        false,
        1,
        newSize,
        jobViewFilterForm.taskStatus.value,
      );

      if (
        data?.code ||
        data?.httpCode ||
        data?.error ||
        job?.code ||
        job?.httpCode ||
        job?.error
      ) {
        showErrorToast(data?.message || "Failed to fetch job tasks data.");
        return;
      }
      updateUpdateEngine({
        selectedJob: job,
        jobTasks: data?.tasks,
        jobViewTotalPages: data?.totalPages,
        jobViewTotalItems: data?.totalElements,
        jobViewCurrentPage: 0,
        jobViewItemsPerPage: newSize,
      });
    } catch (error) {
      showErrorToast(error?.message || "Failed to fetch job tasks data.");
    } finally {
      setLoadingFalse();
    }
  };

  return (
    <DetailsModal
      modalHeader={
        <JobViewModalHeader
          selectedJob={selectedJob}
          jobTasks={jobTasks}
          pendingStatuses={pendingStatuses}
        />
      }
      isOpen={isJobViewModalOpen}
      onClose={onCloseModal}
      footer={
        <div className="job-status-modal-footer">
          <PaginationBar
            totalPages={jobViewTotalPages}
            currentPage={jobViewCurrentPage}
            totalItems={jobViewTotalItems}
            itemsPerPage={jobViewItemsPerPage}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      }
    >
      <JobViewGrid />
    </DetailsModal>
  );
};
